import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Select, Space, message,
  Popconfirm, Tag, Row, Col, Typography, InputNumber
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DesktopOutlined } from '@ant-design/icons';
import { useSimpleStore } from '../stores/simpleStore';
import { getApiUrl } from '../config';

const { Title, Text } = Typography;
const { Option } = Select;

interface MediaFile {
  id: number;
  title: string;
  filename: string;
  original_name: string;
  duration: number;
  mime_type: string;
}

interface CompanyRoute {
  id: number;
  route_number: string;
  route_name: string;
  company_id: number;
}

const HdmiPlaylistManagement: React.FC = () => {
  const { user, selectedCompanyId } = useSimpleStore();
  const [loading, setLoading] = useState(false);
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const [routes, setRoutes] = useState<CompanyRoute[]>([]);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);

  const token = localStorage.getItem('adminToken');
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  // 플레이리스트 목록 로드
  const loadPlaylists = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ playlist_type: 'hdmi' });
      if (selectedCompanyId) params.append('company_id', selectedCompanyId.toString());

      const response = await fetch(getApiUrl(`/api/route-playlists/list?${params}`), { headers });
      const data = await response.json();
      if (data.success) {
        setPlaylists(data.data);
      }
    } catch (error) {
      console.error('HDMI 플레이리스트 로드 에러:', error);
      message.error('HDMI 플레이리스트를 불러오는데 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 노선 목록 로드
  const loadRoutes = async (companyId: number) => {
    try {
      setLoadingRoutes(true);
      const params = new URLSearchParams();
      if (user?.role !== 'admin') params.append('company_id', companyId.toString());

      const response = await fetch(getApiUrl(`/api/company-routes?${params}`), { headers });
      const data = await response.json();
      if (data.success) {
        const filtered = user?.role === 'admin'
          ? data.data.filter((r: CompanyRoute) => r.company_id === companyId)
          : data.data;
        setRoutes(filtered);
      }
    } catch (error) {
      console.error('노선 로드 에러:', error);
    } finally {
      setLoadingRoutes(false);
    }
  };

  // 미디어 파일 로드
  const loadMediaFiles = async (companyId: number) => {
    try {
      setLoadingMedia(true);
      const params = new URLSearchParams({ company_id: companyId.toString(), include_system: 'true', limit: '1000' });
      const response = await fetch(getApiUrl(`/api/media/list?${params}`), { headers });
      const data = await response.json();
      if (data.success) {
        setMediaFiles(data.data.filter((m: MediaFile) =>
          m.mime_type && (m.mime_type.startsWith('video/') || m.mime_type === 'application/mp4')
        ));
      }
    } catch (error) {
      console.error('미디어 로드 에러:', error);
    } finally {
      setLoadingMedia(false);
    }
  };

  useEffect(() => {
    if (selectedCompanyId) {
      loadPlaylists();
      loadRoutes(selectedCompanyId);
      loadMediaFiles(selectedCompanyId);
    }
  }, [selectedCompanyId]);

  // 생성/수정
  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      const requestData = {
        route_number: values.route_number,
        name: `HDMI-${values.route_number}`,
        description: 'HDMI 플레이리스트',
        media_ids: values.media_ids || [],
        playlist_type: 'hdmi',
        company_id: selectedCompanyId
      };

      const response = await fetch(getApiUrl('/api/route-playlists'), {
        method: 'POST',
        headers,
        body: JSON.stringify(requestData)
      });
      const data = await response.json();

      if (data.success) {
        message.success(editingId ? 'HDMI 플레이리스트가 수정되었습니다' : 'HDMI 플레이리스트가 생성되었습니다');
        setModalVisible(false);
        form.resetFields();
        setEditingId(null);
        loadPlaylists();
      } else {
        message.error(data.error || '저장 실패');
      }
    } catch (error) {
      message.error('저장에 실패했습니다');
    } finally {
      setLoading(false);
    }
  };

  // 삭제
  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(getApiUrl(`/api/route-playlists/${id}`), { method: 'DELETE', headers });
      const data = await response.json();
      if (data.success) {
        message.success('삭제되었습니다');
        loadPlaylists();
      } else {
        message.error(data.error || '삭제 실패');
      }
    } catch (error) {
      message.error('삭제에 실패했습니다');
    }
  };

  const columns = [
    {
      title: '노선',
      dataIndex: 'route_number',
      key: 'route_number',
      width: 100,
      render: (v: string) => <Tag color="blue">{v}</Tag>
    },
    {
      title: '플레이리스트명',
      dataIndex: 'playlist_name',
      key: 'playlist_name',
      width: 200
    },
    {
      title: '미디어 수',
      dataIndex: 'media_count',
      key: 'media_count',
      width: 100,
      render: (v: number) => `${v || 0}개`
    },
    {
      title: '총 재생시간',
      dataIndex: 'total_duration',
      key: 'total_duration',
      width: 120,
      render: (v: number) => v ? `${Math.floor(v / 60)}분 ${v % 60}초` : '-'
    },
    {
      title: '작업',
      key: 'actions',
      width: 120,
      render: (_: any, record: any) => (
        <Space>
          <Popconfirm title="삭제하시겠습니까?" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              <DesktopOutlined style={{ marginRight: 8 }} />
              HDMI 플레이리스트 관리
            </Title>
            <Text type="secondary">HDMI 외부 디스플레이 우측 영역에 재생할 동영상 플레이리스트</Text>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { setEditingId(null); form.resetFields(); setModalVisible(true); }}
              disabled={!selectedCompanyId}
            >
              HDMI 플레이리스트 추가
            </Button>
          </Col>
        </Row>

        {!selectedCompanyId && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            좌측 메뉴에서 운수사를 선택해주세요
          </div>
        )}

        {selectedCompanyId && (
          <Table
            columns={columns}
            dataSource={playlists}
            rowKey="id"
            loading={loading}
            size="middle"
          />
        )}
      </Card>

      <Modal
        title={editingId ? 'HDMI 플레이리스트 수정' : 'HDMI 플레이리스트 추가'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="route_number" label="노선 선택" rules={[{ required: true, message: '노선을 선택해주세요' }]}>
            <Select placeholder="노선을 선택하세요" loading={loadingRoutes} showSearch optionFilterProp="children">
              {routes.map(route => (
                <Option key={route.route_number} value={route.route_number}>
                  {route.route_number} - {route.route_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="media_ids" label="동영상 선택 (재생 순서대로)" rules={[{ required: true, message: '동영상을 선택해주세요' }]}>
            <Select
              mode="multiple"
              placeholder="동영상을 순서대로 선택하세요"
              loading={loadingMedia}
              showSearch
              optionFilterProp="children"
            >
              {mediaFiles.map(media => (
                <Option key={media.id} value={media.id}>
                  {media.title || media.original_name} ({media.duration ? `${Math.round(media.duration)}초` : '알 수 없음'})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); }}>취소</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingId ? '수정' : '생성'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default HdmiPlaylistManagement;
